import { logRecommendedFilters } from '@mfs/client/common/log-ga-event/logGaEvent-recommended-filters'
import { listingsFilterCall } from '@mfs/client/listings-client/actions/listings'
import { useIsMobile } from '@mfs/client/listings-client/helpers/useIsMobile'
import { Box, Flex, Hide, Image } from 'pcln-design-system'
import { SubdirectoryArrowRight } from 'pcln-icons'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { getSelected } from '../DynamicFilters/helpers'
import { useChangeHandlers, useStateProps } from '../Hooks'
import { SortOptions } from '../SortOptions'
import { getIsSingleOriginOrDestination, getRecommendedFilters } from './helpers'
import { getDisplayValue } from './helpers/getRecommendedFilters'
import { StyledFilterChip, StyledText, VerticalDivider } from './RecommendedFilters.styles'

/**
 * Recommended Filters Component on 2nd Slice of Round Trip
 * https://priceline.atlassian.net/browse/AIR-61855
 *
 * @param {action} listingsFilterCall - for performing redux filter call
 * @returns <RecommendFilters/>
 */
function RecommendedFilters({ departureSummaryData }) {
  /**
   * Grab filter data from state
   */
  const stateProps = useStateProps()

  const { pclnBootstrapData } = stateProps

  /**
   * Current selected state of chips
   */
  const [selected, setSelected] = useState({})
  const isMobile = useIsMobile()

  /**
   * Obtain the click + change handlers on filter click/select/unselect purposes
   */
  const { originFilter, destinationFilter, handleClick } = useChangeHandlers({
    ...stateProps,
    selected,
    setSelected,
    isRecommended: true,
  })

  /**
   * For sort options
   */

  const dispatch = useDispatch()

  const handleSortOptionChange = () => dispatch(listingsFilterCall(pclnBootstrapData, 'filter'))

  /**
   * Get recommended filters
   */

  const recommendedFilters = getRecommendedFilters(departureSummaryData?.[0], stateProps)
  const isMultiAirline = departureSummaryData?.[0]?.marketingAirlines?.length > 1

  if (!isMobile && isMultiAirline) {
    return null
  }

  const recFiltersToRender = recommendedFilters
    .map((filter) => {
      const { type, filterId, image } = filter

      const chipProps = {
        ...stateProps,
        type,
        filterId,
        selected,
        originFilter,
        destinationFilter,
      }
      const isSelectedFromState = getSelected(chipProps)
      const displayValue = getDisplayValue(chipProps)

      const isHideChip = getIsSingleOriginOrDestination({
        originFilter,
        destinationFilter,
        type,
        filterId,
      })

      if (!isHideChip) {
        return (
          <StyledFilterChip
            title={type}
            label={displayValue}
            align={'center'}
            key={type + '_' + filterId}
            m={'4px'}
            image={
              type === 'AIRLINES' && (
                <Image
                  src={image}
                  alt='recommended-airline-logo'
                  height='15px'
                  width='inherit'
                  minWidth='15px'
                />
              )
            }
            selected={isSelectedFromState}
            onClick={() => {
              handleClick({
                ...chipProps,
                isSelectedFromState,
              })

              logRecommendedFilters({ filterName: type?.toLowerCase() })
            }}
            data-testid='recommended-filter-chip'
            aria-label='recommended-filter-chip'
            showActionIcon={isSelectedFromState}
          />
        )
      }
    })
    .filter(Boolean)

  return recommendedFilters.length ? (
    <Box
      color='background.lightest'
      m='auto'
      maxWidth='1280px'
      data-testid='recommended-filters-container'
      aria-label='recommended-filters-container'
      mb={'16px'}
    >
      <Flex
        alignItems={['center', 'center', 'center', 'center']}
        flexDirection={['row', null, null, 'row']}
        mr={[null, null, null, 0]}
        aria-label='recommended-filters-wrapper'
        overflow='auto'
        pl={[2, 2, 2, 2, 0]}
      >
        <Hide xs>
          <Flex bg='primary.light' borderRadius='2xl' mr={'8px'} width={'28px'} height={'28px'}>
            <SubdirectoryArrowRight size={20} m={'auto'} />
          </Flex>
        </Hide>

        <Hide xs>
          <Box>
            <StyledText fontSize={0} bold mr={1}>
              Find Similar Return:{' '}
            </StyledText>
          </Box>
        </Hide>

        <Hide md lg xl xxl>
          <Box>
            <SortOptions
              key={'sort_options_0'}
              changeHandler={handleSortOptionChange}
              numOfSlices={pclnBootstrapData.searchRequest?.slices?.length}
              isDynamic
            />
          </Box>
        </Hide>

        {recFiltersToRender.length > 0 && (
          <>
            <Hide md lg xl xxl>
              <Box data-testid='vertical-divider' ml={2} mr={2}>
                <VerticalDivider />
              </Box>
            </Hide>

            {recFiltersToRender}
          </>
        )}
      </Flex>
    </Box>
  ) : null
}

export default RecommendedFilters
